import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../store";
import { formatFeetInches, wallsAabb } from "../lib/dim";
import { SheetTabBar } from "./SheetTabBar";
import ManualTraceOverlay from "./ManualTraceOverlay";

/** Read-only blueprint view: draws walls/doors/windows on the blueprint grid */
export default function BlueprintTab() {
  const { blueprint, documents, createSheet, renameSheet, deleteSheet,
          activateSheet, fetchDocumentImage, refreshBlueprint, refreshDocuments } = useStore();
  const sheets = blueprint?.sheets || [];
  const activeSheetId = blueprint?.active_sheet_id;
  const activeSheet = sheets.find((s) => s.id === activeSheetId) || null;
  const walls = blueprint?.walls || [];
  const doors = blueprint?.doors || [];
  const windows = blueprint?.windows || [];

  // Manual-trace overlay state. Only openable when the active sheet has an
  // underlay (i.e. a source_document_id) — otherwise there's no image to
  // trace against.
  const [traceTarget, setTraceTarget] = useState(null);
  const traceableDoc = useMemo(() => {
    const docId = activeSheet?.source_document_id;
    if (!docId) return null;
    return documents.find((d) => d.id === docId) || null;
  }, [activeSheet?.source_document_id, documents]);

  const [underlayUrl, setUnderlayUrl] = useState(null);
  const [underlayOpacity, setUnderlayOpacity] = useState(0.65);
  useEffect(() => {
    let cancelled = false;
    const docId = activeSheet?.source_document_id;
    if (!docId) { setUnderlayUrl(null); return; }
    (async () => {
      // Multi-page PDFs need per-page underlay; source_page comes from the
      // sheet's stored metadata so each sheet shows its own page image.
      const url = await fetchDocumentImage(docId, activeSheet?.source_page || null);
      if (!cancelled) setUnderlayUrl(url);
    })();
    return () => { cancelled = true; };
  }, [activeSheet?.source_document_id, activeSheet?.source_page, fetchDocumentImage]);

  const [showDimensions, setShowDimensions] = useState(true);

  const lastSourceDoc = useMemo(() => {
    const id = blueprint?.last_source_document_id;
    if (!id) return null;
    return documents.find((d) => d.id === id) || null;
  }, [blueprint, documents]);

  const aabb = useMemo(() => wallsAabb(walls), [walls]);
  const fixtures = blueprint?.fixtures || [];
  const labels = blueprint?.labels || [];

  // The Blueprint tab now uses a dynamic viewBox that fits the underlay
  // (if any) OR the walls AABB, so scanned images and traced geometry show
  // up regardless of what unit the AI produced.
  const baseViewBox = useMemo(() => {
    const pad = 4;
    const bf = activeSheet?.building_ft;
    if (bf?.w > 0 && bf?.h > 0) {
      return { x: -pad, y: -pad, w: bf.w + pad * 2, h: bf.h + pad * 2 };
    }
    if (aabb && aabb.w > 0 && aabb.h > 0) {
      return { x: aabb.minX - pad, y: aabb.minY - pad, w: aabb.w + pad * 2, h: aabb.h + pad * 2 };
    }
    // Fallback — legacy 100×100 canvas.
    return { x: -8, y: -8, w: 116, h: 116 };
  }, [aabb, activeSheet?.building_ft]);

  // Zoom / pan state. Resets to baseViewBox whenever the fit changes
  // (i.e. on sheet switch or when new walls arrive). Mouse-wheel zooms
  // around the cursor; click-drag pans; double-click resets.
  const [vb, setVb] = useState(baseViewBox);
  const [panning, setPanning] = useState(null); // {startClient, startVb}
  const svgRef = useRef(null);
  const wheelRef = useRef(null);
  useEffect(() => {
    setVb(baseViewBox);
  }, [baseViewBox.x, baseViewBox.y, baseViewBox.w, baseViewBox.h]);

  const vbStr = `${vb.x} ${vb.y} ${vb.w} ${vb.h}`;
  const zoomPct = Math.round((baseViewBox.w / vb.w) * 100);
  const resetView = () => setVb(baseViewBox);

  wheelRef.current = (e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const [cx, cy] = [vb.x + vb.w * mx, vb.y + vb.h * my];
    const scale = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    // Clamp zoom range: 20× in, 3× out beyond fit.
    const minW = baseViewBox.w / 20;
    const maxW = baseViewBox.w * 3;
    const newW = Math.max(minW, Math.min(maxW, vb.w * scale));
    const newH = newW * (vb.h / vb.w);
    setVb({ x: cx - newW * mx, y: cy - newH * my, w: newW, h: newH });
  };

  // React onWheel is registered passive → preventDefault silently ignored.
  // Attach manually with passive:false.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const h = (e) => wheelRef.current && wheelRef.current(e);
    svg.addEventListener("wheel", h, { passive: false });
    return () => svg.removeEventListener("wheel", h);
  }, []);

  const onMouseDown = (e) => {
    // Left-drag or middle-drag both pan.
    if (e.button === 0 || e.button === 1) {
      setPanning({ startClient: [e.clientX, e.clientY], startVb: { ...vb } });
    }
  };
  const onMouseMove = (e) => {
    if (!panning) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const dx = ((e.clientX - panning.startClient[0]) / rect.width) * panning.startVb.w;
    const dy = ((e.clientY - panning.startClient[1]) / rect.height) * panning.startVb.h;
    setVb({ ...panning.startVb, x: panning.startVb.x - dx, y: panning.startVb.y - dy });
  };
  const onMouseUp = () => setPanning(null);

  // A sheet has SOMETHING renderable if it has walls, an underlay image,
  // any traced labels/fixtures/doors/windows, or a source_document_id — in
  // which case we render the SVG instead of the "empty" placeholder.
  const hasRenderableContent =
    walls.length > 0 ||
    doors.length > 0 ||
    windows.length > 0 ||
    labels.length > 0 ||
    fixtures.length > 0 ||
    !!underlayUrl;

  return (
    <div className="flex flex-col h-full" data-testid="blueprint-tab">
      <SheetTabBar
        sheets={sheets}
        activeSheetId={activeSheetId}
        dirty={false}
        onActivate={activateSheet}
        onCreate={async () => {
          const name = window.prompt("New sheet name:", `Sheet ${sheets.length + 1}`);
          if (!name || !name.trim()) return;
          const fl = parseInt(window.prompt("Floor level (0 = ground, 1 = 2nd floor, -1 = basement):", "0"), 10) || 0;
          const s = await createSheet({ name: name.trim(), floor_level: fl });
          if (s) await activateSheet(s.id);
        }}
        onRename={async (id, currentName) => {
          const name = window.prompt("Rename sheet:", currentName);
          if (name && name.trim() && name !== currentName) await renameSheet(id, { name: name.trim() });
        }}
        onDelete={async (id, name) => {
          if (sheets.length <= 1) { alert("You must keep at least one sheet."); return; }
          if (!window.confirm(`Delete sheet "${name}"? This cannot be undone.`)) return;
          await deleteSheet(id);
        }}
        onChangeFloor={async (id, currentFloor) => {
          const raw = window.prompt("Floor level (-5..50):", String(currentFloor));
          if (raw === null) return;
          const n = parseInt(raw, 10);
          if (Number.isFinite(n)) await renameSheet(id, { floor_level: n });
        }}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] flex-1 min-h-0">
      <section className="relative flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-baseline justify-between">
          <div>
            <div className="label-mono">// VIEW</div>
            <h2 className="font-display text-2xl tracking-tighter">Live Blueprint</h2>
          </div>
          <div className="flex items-center gap-4">
            {traceableDoc && (
              <button
                data-testid="blueprint-trace-walls"
                onClick={() => setTraceTarget({ doc: traceableDoc, sheet: activeSheet })}
                className="label-mono px-3 py-1.5 border border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-colors flex items-center gap-2"
                title="Draw walls manually over the blueprint underlay — AI re-extracts doors/windows/labels around your walls"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 20l16-16M8 4h12v12" />
                </svg>
                TRACE WALLS
              </button>
            )}
            <button
              data-testid="blueprint-dims-toggle"
              onClick={() => setShowDimensions((d) => !d)}
              className={`label-mono px-2 py-1 border ${showDimensions ? "bg-[#FF6600] text-black border-[#FF6600]" : "border-white/20 hover:bg-white/10"}`}
            >DIMS {showDimensions ? "ON" : "OFF"}</button>
            <div className="label-mono">
              {walls.length} walls · {doors.length} doors · {windows.length} windows
              {aabb && showDimensions && (
                <span className="text-[#FF6600] ml-3">
                  {formatFeetInches(aabb.w)} × {formatFeetInches(aabb.h)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 min-h-0">
          <div className="w-full h-full blueprint-canvas-bg relative">
            {underlayUrl && (
              <div className="absolute top-2 left-2 z-10 flex items-center gap-2 bg-black/70 border border-white/20 rounded px-2 py-1 text-white text-[10px] font-mono uppercase tracking-wider">
                <span>UNDERLAY</span>
                <input
                  data-testid="blueprint-underlay-opacity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={underlayOpacity}
                  onChange={(e) => setUnderlayOpacity(parseFloat(e.target.value))}
                  className="w-24 accent-[#FFCC00]"
                />
                <span className="tabular-nums w-9 text-right">{Math.round(underlayOpacity * 100)}%</span>
              </div>
            )}
            {hasRenderableContent && (
              <div
                data-testid="blueprint-zoom-controls"
                className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/70 border border-white/20 rounded px-2 py-1 text-white text-[10px] font-mono uppercase tracking-wider"
              >
                <button
                  data-testid="blueprint-zoom-out"
                  onClick={() => setVb((v) => {
                    const newW = Math.min(baseViewBox.w * 3, v.w * 1.25);
                    const newH = newW * (v.h / v.w);
                    return { x: v.x + (v.w - newW) / 2, y: v.y + (v.h - newH) / 2, w: newW, h: newH };
                  })}
                  className="w-6 h-6 flex items-center justify-center border border-white/20 hover:bg-white/10"
                  title="Zoom out"
                >−</button>
                <span data-testid="blueprint-zoom-pct" className="tabular-nums w-10 text-center">{zoomPct}%</span>
                <button
                  data-testid="blueprint-zoom-in"
                  onClick={() => setVb((v) => {
                    const newW = Math.max(baseViewBox.w / 20, v.w / 1.25);
                    const newH = newW * (v.h / v.w);
                    return { x: v.x + (v.w - newW) / 2, y: v.y + (v.h - newH) / 2, w: newW, h: newH };
                  })}
                  className="w-6 h-6 flex items-center justify-center border border-white/20 hover:bg-white/10"
                  title="Zoom in"
                >+</button>
                <button
                  data-testid="blueprint-zoom-reset"
                  onClick={resetView}
                  className="ml-1 px-2 h-6 flex items-center border border-white/20 hover:bg-white/10"
                  title="Fit to view (or double-click canvas)"
                >FIT</button>
              </div>
            )}
            {!hasRenderableContent ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-blue-200/40 font-mono">
                  <div className="label-mono text-blue-200/60 mb-2">// AWAITING ANALYSIS</div>
                  <div>Upload a blueprint to populate this view.</div>
                </div>
              </div>
            ) : (
              <svg
                ref={svgRef}
                viewBox={vbStr}
                className={`w-full h-full ${panning ? "cursor-grabbing" : "cursor-grab"}`}
                style={{ touchAction: "none" }}
                preserveAspectRatio="xMidYMid meet"
                data-testid="blueprint-svg"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onDoubleClick={resetView}
              >
                {/* Blueprint underlay — falls back to a 100×100 ft canvas
                    when we don't yet have building_ft or wall geometry,
                    so a scanned image ALWAYS renders, not a blank page. */}
                {underlayUrl && (() => {
                  const bf = activeSheet?.building_ft;
                  let x = 0, y = 0, w = 100, h = 100;
                  if (bf?.w > 0 && bf?.h > 0) {
                    w = bf.w; h = bf.h;
                  } else if (aabb && aabb.w > 0 && aabb.h > 0) {
                    x = aabb.minX; y = aabb.minY; w = aabb.w; h = aabb.h;
                  }
                  return (
                    <image
                      data-testid="blueprint-underlay"
                      href={underlayUrl}
                      x={x} y={y} width={w} height={h}
                      preserveAspectRatio="none"
                      opacity={underlayOpacity}
                    />
                  );
                })()}
                {/* Walls — professional double-line style on the blueprint
                    (cyanotype) canvas. White fill (poché) + thin outline. */}
                {walls.map((w) => {
                  if (!w.start || !w.end) return null;
                  const dx = w.end[0] - w.start[0];
                  const dy = w.end[1] - w.start[1];
                  const len = Math.hypot(dx, dy) || 1;
                  const nx = -dy / len;
                  const ny = dx / len;
                  const t = w.thickness || 0.5;
                  const half = t / 2;
                  const p1 = [w.start[0] + nx * half, w.start[1] + ny * half];
                  const p2 = [w.end[0]   + nx * half, w.end[1]   + ny * half];
                  const p3 = [w.end[0]   - nx * half, w.end[1]   - ny * half];
                  const p4 = [w.start[0] - nx * half, w.start[1] - ny * half];
                  return (
                    <polygon
                      key={w.id}
                      points={`${p1[0]},${p1[1]} ${p2[0]},${p2[1]} ${p3[0]},${p3[1]} ${p4[0]},${p4[1]}`}
                      fill="#F5F5F0"
                      stroke="#FFFFFF"
                      strokeWidth={Math.max(0.06, t * 0.18)}
                      strokeLinejoin="miter"
                    />
                  );
                })}
                {/* Doors */}
                {doors.map((d) => (
                  <circle
                    key={d.id}
                    cx={d.position[0]}
                    cy={d.position[1]}
                    r={(d.width || 3) / 4}
                    fill="#FFCC00"
                    stroke="#FFCC00"
                    strokeWidth="0.3"
                  />
                ))}
                {/* Windows */}
                {windows.map((w) => (
                  <rect
                    key={w.id}
                    x={w.position[0] - (w.width || 4) / 2}
                    y={w.position[1] - 0.4}
                    width={w.width || 4}
                    height={0.8}
                    fill="#0055FF"
                  />
                ))}

                {/* AI-traced fixtures */}
                {fixtures.map((f) => {
                  const [w, h] = f.size || [2, 2];
                  const rot = f.rotation_deg || 0;
                  return (
                    <g key={f.id} transform={`translate(${f.position[0]},${f.position[1]}) rotate(${rot})`}>
                      <rect x={-w / 2} y={-h / 2} width={w} height={h}
                        fill="#8FA8C0" fillOpacity="0.35" stroke="#8FA8C0" strokeWidth="0.1" />
                      <text x={0} y={0.3} fontSize={Math.min(w, h) * 0.32}
                        fill="#F5F5F5" textAnchor="middle"
                        fontFamily="IBM Plex Mono, monospace" fontWeight="700">
                        {(f.kind || "").toUpperCase()}
                      </text>
                    </g>
                  );
                })}

                {/* Room / dimension labels */}
                {labels.map((l) => (
                  <text
                    key={l.id}
                    x={l.position[0]}
                    y={l.position[1]}
                    fontSize={1.6}
                    fill="#F5F5F0"
                    textAnchor="middle"
                    fontFamily="IBM Plex Mono, monospace"
                    fontWeight="700"
                  >
                    {l.text}
                  </text>
                ))}

                {/* Per-wall dimensions */}
                {showDimensions && walls.map((w) => {
                  if (!w.start || !w.end) return null;
                  const dx = w.end[0] - w.start[0];
                  const dy = w.end[1] - w.start[1];
                  const len = Math.hypot(dx, dy);
                  if (len < 1) return null;
                  const mx = (w.start[0] + w.end[0]) / 2;
                  const my = (w.start[1] + w.end[1]) / 2;
                  const nx = -dy / len, ny = dx / len;
                  const off = 1.8;
                  const tx = mx + nx * off;
                  const ty = my + ny * off;
                  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                  const flip = angle > 90 || angle < -90 ? 180 : 0;
                  return (
                    <g key={`dim-${w.id}`} pointerEvents="none">
                      <line x1={w.start[0]} y1={w.start[1]} x2={w.start[0] + nx * off * 0.9} y2={w.start[1] + ny * off * 0.9} stroke="#7AB8FF" strokeWidth="0.08" />
                      <line x1={w.end[0]} y1={w.end[1]} x2={w.end[0] + nx * off * 0.9} y2={w.end[1] + ny * off * 0.9} stroke="#7AB8FF" strokeWidth="0.08" />
                      <line x1={w.start[0] + nx * off} y1={w.start[1] + ny * off} x2={w.end[0] + nx * off} y2={w.end[1] + ny * off} stroke="#7AB8FF" strokeWidth="0.08" />
                      <text x={tx} y={ty} fontSize="1.6" fill="#7AB8FF" textAnchor="middle"
                        transform={`rotate(${angle + flip}, ${tx}, ${ty})`}
                        fontFamily="IBM Plex Mono, monospace" fontWeight="600">
                        {formatFeetInches(len)}
                      </text>
                    </g>
                  );
                })}

                {/* Overall outer dimension chain */}
                {showDimensions && aabb && aabb.w >= 1 && aabb.h >= 1 && (() => {
                  const outer = 5.5;
                  const tick = 0.9;
                  return (
                    <g pointerEvents="none">
                      <line x1={aabb.minX} y1={aabb.minY - outer} x2={aabb.maxX} y2={aabb.minY - outer} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.minX} y1={aabb.minY - outer - tick / 2} x2={aabb.minX} y2={aabb.minY - outer + tick / 2} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.maxX} y1={aabb.minY - outer - tick / 2} x2={aabb.maxX} y2={aabb.minY - outer + tick / 2} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.minX} y1={aabb.minY} x2={aabb.minX} y2={aabb.minY - outer + tick} stroke="#FF6600" strokeWidth="0.07" strokeDasharray="0.5 0.5" />
                      <line x1={aabb.maxX} y1={aabb.minY} x2={aabb.maxX} y2={aabb.minY - outer + tick} stroke="#FF6600" strokeWidth="0.07" strokeDasharray="0.5 0.5" />
                      <text x={(aabb.minX + aabb.maxX) / 2} y={aabb.minY - outer - 1.1} fontSize="2.4" fill="#FF6600" textAnchor="middle"
                        fontFamily="IBM Plex Mono, monospace" fontWeight="700">
                        {formatFeetInches(aabb.w)}
                      </text>
                      <line x1={aabb.minX - outer} y1={aabb.minY} x2={aabb.minX - outer} y2={aabb.maxY} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.minX - outer - tick / 2} y1={aabb.minY} x2={aabb.minX - outer + tick / 2} y2={aabb.minY} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.minX - outer - tick / 2} y1={aabb.maxY} x2={aabb.minX - outer + tick / 2} y2={aabb.maxY} stroke="#FF6600" strokeWidth="0.14" />
                      <line x1={aabb.minX} y1={aabb.minY} x2={aabb.minX - outer + tick} y2={aabb.minY} stroke="#FF6600" strokeWidth="0.07" strokeDasharray="0.5 0.5" />
                      <line x1={aabb.minX} y1={aabb.maxY} x2={aabb.minX - outer + tick} y2={aabb.maxY} stroke="#FF6600" strokeWidth="0.07" strokeDasharray="0.5 0.5" />
                      <text x={aabb.minX - outer - 1.1} y={(aabb.minY + aabb.maxY) / 2} fontSize="2.4" fill="#FF6600" textAnchor="middle"
                        transform={`rotate(-90, ${aabb.minX - outer - 1.1}, ${(aabb.minY + aabb.maxY) / 2})`}
                        fontFamily="IBM Plex Mono, monospace" fontWeight="700">
                        {formatFeetInches(aabb.h)}
                      </text>
                    </g>
                  );
                })()}
              </svg>
            )}
          </div>
        </div>
      </section>

      {/* Right side panel */}
      <aside className="border-l border-white/10 p-6 overflow-y-auto">
        <div className="label-mono mb-2">// LEGEND</div>
        <div className="space-y-2 mb-8">
          <LegendRow color="#FFFFFF" label="Walls" />
          <LegendRow color="#FFCC00" label="Doors" />
          <LegendRow color="#0055FF" label="Windows" />
          <LegendRow color="#7AB8FF" label="Wall dimensions" />
          <LegendRow color="#FF6600" label="Overall dimensions" />
        </div>

        <div className="label-mono mb-2">// LAST SYNC</div>
        {lastSourceDoc ? (
          <div className="border border-white/10 p-4 bg-[#141414]">
            <div className="font-medium truncate">{lastSourceDoc.filename}</div>
            <div className="label-mono mt-2">{lastSourceDoc.doc_type}</div>
            {lastSourceDoc.analysis?.summary && (
              <p className="text-xs text-neutral-400 mt-3 leading-relaxed">
                {lastSourceDoc.analysis.summary}
              </p>
            )}
            {lastSourceDoc.analysis?.rooms?.length > 0 && (
              <>
                <div className="label-mono mt-4 mb-2">ROOMS</div>
                <div className="space-y-1">
                  {lastSourceDoc.analysis.rooms.slice(0, 8).map((r, i) => (
                    <div key={i} className="flex justify-between text-xs font-mono">
                      <span className="text-neutral-300">{r.name}</span>
                      <span className="text-neutral-500">{r.approx_area_sqft || "—"} ft²</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-neutral-500 text-sm font-mono">No analysis yet.</div>
        )}
      </aside>
      </div>
      {traceTarget && (
        <ManualTraceOverlay
          doc={traceTarget.doc}
          sheet={traceTarget.sheet}
          onClose={() => setTraceTarget(null)}
          onSaved={async () => {
            await refreshBlueprint();
            await refreshDocuments();
          }}
        />
      )}
    </div>
  );
}

function LegendRow({ color, label }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-4 h-4" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
